<div class="wrapper-customers">
  <!-- ========================================== -->
  <!-- VIEW 1: CUSTOMERS LISTING TABLE / GRID -->
  <!-- ========================================== -->
  <div class="customer-listing-view" *ngIf="currentView === 'list'">
    <!-- Page Header (Standalone View) -->
    <div class="customer-page-header mb-4" *ngIf="!embedded">
      <div class="header-left">
        <div class="d-flex align-items-center gap-3">
          <h1 class="page-title mb-0">{{ "Customers" | translate }}</h1>
          <span class="customer-count-badge" *ngIf="allCustomers.length">
            {{ totalCustomersCount }} {{ "Total" | translate }}
          </span>
        </div>
        <p class="page-subtitle text-muted mt-1 mb-0">
          {{ "Track customer lifetime value, purchase frequency, and build stronger relationships." | translate }}
        </p>
      </div>

      <!-- Quick Action Navigation in Header -->
      <div class="header-actions topbar-actions d-flex align-items-center flex-wrap gap-2">
        <button
          type="button"
          class="btn btn-action-ai"
          data-ai-purpose="generate summary"
          [disabled]="dataIsLoading || isGeneratingAiSummary"
          (click)="generateAiSummary($event)"
          [title]="'Summarize Customer Insights with AI Assistant' | translate"
        >
          <i class="fi fi-rr-sparkles mr-1" [class.fa-spin]="isGeneratingAiSummary"></i>
          <span>{{ (isGeneratingAiSummary ? "Generating Summary..." : "AI Summary") | translate }}</span>
          <span class="ai-coin-token" *ngIf="!isGeneratingAiSummary && aiTokenCost" [attr.title]="aiTokenCost + ' tokens'">
            {{ aiTokenCost }}
          </span>
        </button>
      </div>
    </div>

    <!-- Embedded Header Action Bar (when embedded in Customers & Carts) -->
    <div class="customer-embedded-header d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3" *ngIf="embedded">
      <div class="d-flex align-items-center gap-2">
        <span class="customer-count-badge" *ngIf="allCustomers.length">
          {{ totalCustomersCount }} {{ "Total Customers" | translate }}
        </span>
        <span class="text-muted text-xs d-none d-md-inline">
          {{ "Track customer lifetime value, purchase frequency, and build stronger relationships." | translate }}
        </span>
      </div>
      <div class="header-actions topbar-actions d-flex align-items-center flex-wrap gap-2 ml-auto">
        <button
          type="button"
          class="btn btn-action-ai"
          data-ai-purpose="generate summary"
          [disabled]="dataIsLoading || isGeneratingAiSummary"
          (click)="generateAiSummary($event)"
          [title]="'Summarize Customer Insights with AI Assistant' | translate"
        >
          <i class="fi fi-rr-sparkles mr-1" [class.fa-spin]="isGeneratingAiSummary"></i>
          <span>{{ (isGeneratingAiSummary ? "Generating Summary..." : "AI Summary") | translate }}</span>
          <span class="ai-coin-token" *ngIf="!isGeneratingAiSummary && aiTokenCost" [attr.title]="aiTokenCost + ' tokens'">
            {{ aiTokenCost }}
          </span>
        </button>
      </div>
    </div>

    <!-- AI Customer Summary & Insights Card -->
    <div
      class="ai-summary-card mb-4"
      *ngIf="showAiSummaryCard"
    >
      <div class="ai-summary-header d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div class="d-flex align-items-center">
          <div class="ai-icon-badge mr-2">
            <i class="fi fi-rr-sparkles"></i>
          </div>
          <div>
            <div class="ai-title font-weight-bold">
              {{ "AI Customer Summary & Insights" | translate }}
            </div>
            <div class="ai-subtitle text-muted">
              {{ "A plain-language look at who buys from you, who might not come back, and what to do next" | translate }}
            </div>
          </div>
        </div>

        <div class="ai-actions d-flex align-items-center gap-2">
          <button
            *ngIf="!isGeneratingAiSummary && aiSummaryContent && !aiSummaryError"
            type="button"
            class="btn btn-sm btn-ai-action"
            (click)="copyAiSummary()"
            [title]="'Copy to Clipboard' | translate"
          >
            <i class="fi" [ngClass]="isCopied ? 'fi-rr-check' : 'fi-rr-copy'"></i>
            <span class="ml-1">{{ (isCopied ? "Copied!" : "Copy") | translate }}</span>
          </button>
          <button
            *ngIf="!isGeneratingAiSummary"
            type="button"
            class="btn btn-sm btn-ai-action"
            data-ai-purpose="generate summary"
            (click)="generateAiSummary($event)"
            [title]="'Regenerate Summary' | translate"
          >
            <i class="fi fi-rr-refresh"></i>
            <span class="ml-1">{{ "Regenerate" | translate }}</span>
            <span class="ai-coin-token ai-coin-sm" *ngIf="aiTokenCost" [attr.title]="aiTokenCost + ' tokens'">
              {{ aiTokenCost }}
            </span>
          </button>
          <button
            type="button"
            class="btn btn-sm btn-ai-close"
            (click)="dismissAiSummary()"
            [attr.aria-label]="'Close' | translate"
          >
            <i class="fi fi-rr-cross"></i>
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div *ngIf="isGeneratingAiSummary" class="ai-loading-container py-4 text-center">
        <div class="ai-spinner mb-3">
          <i class="fi fi-rr-sparkles fa-spin"></i>
        </div>
        <div class="font-weight-bold text-primary mb-1">
          {{ "Analyzing customer cohort metrics & generating insights..." | translate }}
        </div>
        <div class="text-muted small">
          {{ "Evaluating lifetime spend, repeat purchase frequency, and retention opportunities" | translate }}
        </div>
        <div class="ai-skeleton-box mt-3">
          <div class="skeleton-line full"></div>
          <div class="skeleton-line three-quarters"></div>
          <div class="skeleton-line half"></div>
        </div>
      </div>

      <!-- Error State -->
      <div *ngIf="!isGeneratingAiSummary && aiSummaryError" class="ai-error-container p-3 my-2">
        <div class="d-flex align-items-center text-danger mb-2">
          <i class="fi fi-rr-exclamation mr-2"></i>
          <span class="font-weight-bold">{{ aiSummaryError }}</span>
        </div>
        <button type="button" class="btn btn-sm btn-primary mt-1" data-ai-purpose="generate summary" (click)="generateAiSummary($event)">
          {{ "Try Again" | translate }}
        </button>
      </div>

      <!-- AI Content Display -->
      <div
        *ngIf="!isGeneratingAiSummary && !aiSummaryError && aiSummaryContent"
        class="ai-summary-body mt-3"
      >
        <!-- Structured Insights Cards Grid -->
        <div class="ai-insights-grid" *ngIf="parsedSections && parsedSections.length > 0">
          <div
            *ngFor="let sec of parsedSections"
            class="ai-insight-card"
            [ngClass]="'theme-' + sec.theme"
          >
            <div class="ai-card-header">
              <span class="ai-card-badge">
                <i class="fi" [ngClass]="sec.icon"></i>
              </span>
              <h4 class="ai-card-title">{{ sec.title | translate }}</h4>
            </div>
            <div class="ai-card-body">
              <div *ngFor="let row of sec.rows" class="ai-row-item">
                <span
                  *ngIf="sec.theme === 'recommendations' && row.index"
                  class="item-num-badge"
                >{{ row.index }}</span>
                <span
                  *ngIf="sec.theme !== 'recommendations' || !row.index"
                  class="item-bullet"
                ></span>
                <div class="item-content">
                  <span *ngIf="row.label" class="ai-label">{{ row.label }}:</span>
                  <span *ngIf="row.value" class="ai-val-highlight ml-1">{{ row.value }}</span>
                  <span *ngIf="row.note" class="ai-note ml-1"><ng-container *ngIf="row.value">– </ng-container>{{ row.note }}</span>
                  <span *ngIf="row.text && !row.label" class="ai-text">{{ row.text }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Raw Fallback if no sections were parsed -->
        <div *ngIf="!parsedSections || parsedSections.length === 0" class="ai-fallback-text p-3">
          <div class="small text-muted" style="white-space: pre-wrap;">{{ aiSummaryContent }}</div>
        </div>
      </div>
    </div>

    <!-- KPI Summary Metrics Cards -->
    <div class="customer-kpi-grid mb-4" *ngIf="!dataIsLoading && allCustomers.length">
      <!-- Card 1: Total Customers -->
      <div class="kpi-card shadow-sm">
        <div class="kpi-icon-wrap icon-blue">
          <i class="fi fi-rr-users-alt"></i>
        </div>
        <div class="kpi-info">
          <div class="d-flex align-items-center justify-content-between mb-1">
            <span class="kpi-label mb-0">{{ "Total Customers" | translate }}</span>
            <span class="kpi-scope-pill">{{ activePeriodDisplay }}</span>
          </div>
          <div class="kpi-value-row">
            <span class="kpi-value">{{ totalCustomersCount | number }}</span>
            <span
              class="kpi-badge"
              *ngIf="customerGrowthRate.hasBadge !== false"
              [ngClass]="{
                'badge-growth': customerGrowthRate.isPositive && !customerGrowthRate.isNeutral,
                'badge-neutral': customerGrowthRate.isNeutral,
                'badge-danger': !customerGrowthRate.isPositive
              }"
            >
              <i
                class="fi"
                [ngClass]="{
                  'fi-rr-arrow-trend-up': customerGrowthRate.isPositive && !customerGrowthRate.isNeutral && !customerGrowthRate.isNew,
                  'fi-rr-sparkles': customerGrowthRate.isNew,
                  'fi-rr-arrow-trend-down': !customerGrowthRate.isPositive,
                  'fi-rr-minus': customerGrowthRate.isNeutral
                }"
              ></i>
              {{ customerGrowthRate.isNew ? ('New' | translate) : customerGrowthRate.percent }}
            </span>
          </div>
          <span class="kpi-subtext text-muted">
            {{ newCustomersThisMonthCount }} {{ "new in this period" | translate }}
            <span class="compare-label text-xs ml-1" *ngIf="customerGrowthRate.compareLabel">({{ customerGrowthRate.compareLabel | translate }})</span>
          </span>
        </div>
      </div>

      <!-- Card 2: Repeat Customer Rate -->
      <div class="kpi-card shadow-sm">
        <div class="kpi-icon-wrap icon-emerald">
          <i class="fi fi-rr-refresh"></i>
        </div>
        <div class="kpi-info">
          <div class="d-flex align-items-center justify-content-between mb-1">
            <span class="kpi-label mb-0">{{ "Repeat Purchase Rate" | translate }}</span>
            <span class="kpi-scope-pill">{{ activePeriodDisplay }}</span>
          </div>
          <div class="kpi-value-row">
            <span class="kpi-value">{{ repeatCustomerRate }}%</span>
            <span class="kpi-badge badge-neutral">
              {{ repeatCustomersCount }} {{ (repeatCustomersCount === 1 ? 'buyer' : 'buyers') | translate }}
            </span>
          </div>
          <span class="kpi-subtext text-muted">{{ repeatCustomersCount }} {{ "of" | translate }} {{ payingBuyersCount }} {{ "paying buyers are repeat customers" | translate }}</span>
        </div>
      </div>

      <!-- Card 3: Average Lifetime Value (LTV) -->
      <div class="kpi-card shadow-sm">
        <div class="kpi-icon-wrap icon-amber">
          <i class="fi fi-rr-coins"></i>
        </div>
        <div class="kpi-info">
          <div class="d-flex align-items-center justify-content-between mb-1">
            <span class="kpi-label mb-0">{{ "Avg. Customer Spend" | translate }}</span>
            <span class="kpi-scope-pill">{{ activePeriodDisplay }}</span>
          </div>
          <div class="kpi-value-row">
            <span class="kpi-value">{{ kpiCurrency }} {{ averageLifetimeValue | number : '1.2-2' }}</span>
          </div>
          <span class="kpi-subtext text-muted" *ngFor="let other of otherCurrencyAverageSpend">
            {{ other.currency }} {{ other.average_spend | number : '1.2-2' }} · {{ other.buyers }} {{ (other.buyers === 1 ? 'buyer' : 'buyers') | translate }}
          </span>
          <span class="kpi-subtext text-muted">{{ "Average spend in period" | translate }}</span>
        </div>
      </div>

      <!-- Card 4: VIP / High Spenders -->
      <div class="kpi-card shadow-sm" [title]="'Lifetime spend ≥ MYR 2,000 or ≥ 10 paid orders' | translate">
        <div class="kpi-icon-wrap icon-purple">
          <i class="fi fi-rr-crown"></i>
        </div>
        <div class="kpi-info">
          <div class="d-flex align-items-center justify-content-between mb-1">
            <span class="kpi-label mb-0">{{ "VIP High Spenders" | translate }}</span>
            <span class="kpi-scope-pill">{{ activePeriodDisplay }}</span>
          </div>
          <div class="kpi-value-row">
            <span class="kpi-value">{{ vipCustomersCount }}</span>
            <span
              class="kpi-badge badge-vip"
              *ngIf="vipCustomersCount > 0"
              [title]="'Lifetime spend ≥ MYR 2,000 or ≥ 10 paid orders' | translate"
            >
              <i class="fi fi-rr-star"></i> {{ "Top Tier" | translate }}
            </span>
          </div>
          <span class="kpi-subtext text-muted">{{ "VIP customers who bought in period" | translate }}</span>
        </div>
      </div>
    </div>

    <!-- Filter & Search Toolbar (Abandoned Cart & Order Style) -->
    <div class="checkout-toolbar mb-4" [class.is-collapsed]="!toolbarOpen">
      <div class="filter-options" id="customerFilterOptions">
        <!-- Date Filter Dropdown -->
        <div ngbDropdown #dateDropdown="ngbDropdown" class="d-inline-block" [autoClose]="'outside'">
          <button
            class="btn btn-filter"
            [class.active]="dateFilter?.value !== 'cm' || (fromDate && toDate)"
            id="dropdownDateFilter"
            ngbDropdownToggle
            title="{{ 'Filter customers by order date' | translate }}"
          >
            <i class="fi fi-rr-calendar mr-1"></i>
            <span>
              {{ "Order Date" | translate }}: {{ date.type?.display || (dateFilter?.display | translate) || ("Current Month" | translate) }}
            </span>
          </button>
          <div ngbDropdownMenu aria-labelledby="dropdownDateFilter" class="custom-dropdown p-2" style="min-width: 290px;">
            <div class="px-2 py-1.5 mb-1 bg-light rounded text-xs text-muted d-flex align-items-center">
              <i class="fi fi-rr-info mr-1.5 text-primary"></i>
              <span><strong>{{ "Order Date" | translate }}:</strong> {{ "Filter by date orders were placed" | translate }}</span>
            </div>
            <button ngbDropdownItem *ngFor="let filter of DATE_FILTERS" [disabled]="filter.selectable"
              (click)="dateUpdated(filter); dateDropdown.close()">
              {{ filter.display | translate }}
            </button>
            <div class="dropdown-divider"></div>
            <div class="px-2 date-range-picker" (click)="$event.stopPropagation()">
              <p class="mb-2 font-weight-bold text-xs">{{ "Custom Order Date Range" | translate }}:</p>
              <ngb-datepicker #dp (dateSelect)="onDateSelection($event)" [displayMonths]="1"
                [minDate]="this.minDate" [maxDate]="this.maxDate" [dayTemplate]="t" outsideDays="hidden">
              </ngb-datepicker>

              <ng-template #t let-date let-focused="focused">
                <div class="custom-day-container" [class.focused]="focused" [class.start-day]="date.equals(fromDate)"
                  [class.end-day]="date.equals(toDate)" [class.range]="isRange(date)"
                  [class.faded]="isHovered(date) || isInside(date)" (mouseenter)="hoveredDate = date"
                  (mouseleave)="hoveredDate = null">
                  <span class="custom-day">{{ date.day }}</span>
                </div>
              </ng-template>
              <div class="py-2 text-right">
                <button type="button" class="btn btn-date-range cancel mr-1" (click)="dateDropdown.close()">
                  {{ "Cancel" | translate }}
                </button>
                <button type="button" class="btn btn-date-range" [disabled]="!fromDate || !toDate"
                  (click)="setDueDate(); dateDropdown.close()">
                  {{ "Apply" | translate }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Country Filter Dropdown -->
        <div ngbDropdown class="d-inline-block" *ngIf="customerCountry?.length">
          <button
            class="btn btn-filter"
            [class.active]="filteredCountry !== ''"
            id="dropdownCountry"
            ngbDropdownToggle
          >
            <i class="fi fi-rr-globe mr-1"></i>
            <span>
              {{
                filteredCountry !== ""
                  ? (getCountryName(filteredCountry) | translate)
                  : ("All Countries" | translate)
              }}
            </span>
          </button>
          <div ngbDropdownMenu aria-labelledby="dropdownCountry" class="custom-dropdown" style="min-width: 220px; max-height: 320px; overflow-y: auto;">
            <button
              ngbDropdownItem
              *ngFor="let c of customerCountry"
              (click)="changeCountry(c.country_code)"
              class="py-2"
            >
              <div class="d-flex align-items-center">
                <img
                  *ngIf="c.country_code"
                  [src]="getFlagImageURL(c.country_code)"
                  class="mr-2 flex-shrink-0"
                  style="width: 20px; height: 14px; object-fit: cover; border-radius: 2px;"
                />
                <i *ngIf="!c.country_code" class="fi fi-rr-globe mr-2 text-muted" style="font-size: 14px;"></i>
                <span class="text-truncate">{{ getCountryName(c.country_code) | translate }}</span>
              </div>
            </button>
          </div>
        </div>

        <!-- Sort Dropdown -->
        <div ngbDropdown class="d-inline-block">
          <button class="btn btn-filter" ngbDropdownToggle id="dropdownSort">
            <i class="fi fi-rr-sort-alt mr-1"></i>
            <span *ngIf="sortField === 'total_spent'">{{ "Highest Spend" | translate }}</span>
            <span *ngIf="sortField === 'total_orders'">{{ "Most Orders" | translate }}</span>
            <span *ngIf="sortField === 'last_order_date'">{{ "Recently Active" | translate }}</span>
            <span *ngIf="sortField === 'customer_name'">{{ "Name (A-Z)" | translate }}</span>
          </button>
          <div ngbDropdownMenu aria-labelledby="dropdownSort" class="custom-dropdown">
            <button ngbDropdownItem (click)="setSort('total_spent')" [class.active]="sortField === 'total_spent'">
              <i class="fi fi-rr-coins mr-2 text-warning"></i>
              {{ "Highest Spend" | translate }}
            </button>
            <button ngbDropdownItem (click)="setSort('total_orders')" [class.active]="sortField === 'total_orders'">
              <i class="fi fi-rr-shopping-bag mr-2 text-primary"></i>
              {{ "Most Orders" | translate }}
            </button>
            <button ngbDropdownItem (click)="setSort('last_order_date')" [class.active]="sortField === 'last_order_date'">
              <i class="fi fi-rr-clock mr-2 text-success"></i>
              {{ "Recently Active" | translate }}
            </button>
            <button ngbDropdownItem (click)="setSort('customer_name')" [class.active]="sortField === 'customer_name'">
              <i class="fi fi-rr-sort-alpha-down mr-2 text-info"></i>
              {{ "Name (A-Z)" | translate }}
            </button>
          </div>
        </div>

        <!-- Export Excel Button (temporarily hidden, see showExportButton) -->
        <button
          *ngIf="showExportButton"
          type="button"
          class="btn btn-filter btn-export-excel"
          [disabled]="!allCustomers.length"
          (click)="download('csv')"
          title="{{ 'Export to Excel' | translate }}"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="mr-1.5 flex-shrink-0">
            <path fill="#107C41" d="M14 2H6C4.89 2 4 2.89 4 4V20C4 21.11 4.89 22 6 22H18C19.11 22 20 21.11 20 20V8L14 2Z"/>
            <path fill="#FFFFFF" opacity="0.6" d="M14 2L20 8H14V2Z"/>
            <path fill="#FFFFFF" d="M9.6 16.5L11.5 13.6L13.4 16.5H15.5L12.6 12.3L15.3 8.5H13.2L11.5 11L9.8 8.5H7.7L10.4 12.3L7.5 16.5H9.6Z"/>
          </svg>
          <span class="text-[#107C41] font-weight-medium">{{ "Export Excel" | translate }}</span>
        </button>
      </div>

      <!-- Search, view switcher and, on phones, the Filters button that folds the filters above -->
      <div class="toolbar-end">
        <!-- Search Input Box -->
        <div class="search-box">
          <input
            type="text"
            class="form-control"
            [(ngModel)]="mobileOrEmail"
            (input)="searchMobileOrEmail($event)"
            placeholder="{{ 'Search by Name, Email, Phone, City...' | translate }}"
          />
          <i class="fi fi-rr-search search-icon"></i>
          <i class="fas fa-times clear-icon" *ngIf="mobileOrEmail" (click)="clearCustomerSearch()"></i>
        </div>

        <!-- View Switcher (Table vs Grid) -->
        <div class="view-mode-toggle">
          <button
            type="button"
            class="btn-view-mode"
            [class.active]="viewMode === 'table'"
            (click)="toggleViewMode('table')"
            title="{{ 'Table View' | translate }}"
          >
            <i class="fi fi-rr-list"></i>
          </button>
          <button
            type="button"
            class="btn-view-mode"
            [class.active]="viewMode === 'grid'"
            (click)="toggleViewMode('grid')"
            title="{{ 'Grid Cards View' | translate }}"
          >
            <i class="fi fi-rr-grid"></i>
          </button>
        </div>

        <button
          type="button"
          class="btn toolbar-toggle"
          [class.active]="toolbarFilterCount > 0"
          [class.is-open]="toolbarOpen"
          [attr.aria-expanded]="toolbarOpen"
          aria-controls="customerFilterOptions"
          (click)="toolbarOpen = !toolbarOpen"
        >
          <i class="fi fi-rr-filter" aria-hidden="true"></i>
          <span>{{ "Filters" | translate }}</span>
          <span class="filter-count" *ngIf="toolbarFilterCount > 0">{{ toolbarFilterCount }}</span>
          <i class="fi fi-rr-angle-small-down toggle-chevron" aria-hidden="true"></i>
        </button>
      </div>
    </div>

    <!-- Main Content Area -->
    <div class="customer-content-area">
      <!-- Loading Spinner -->
      <div class="row" *ngIf="dataIsLoading; else loadedContent">
        <div class="loading-container col-12 text-center py-5 my-5">
          <mat-spinner class="mx-auto" [diameter]="48"></mat-spinner>
          <p class="mt-3 text-muted">{{ "Loading customer insights..." | translate }}</p>
        </div>
      </div>

      <ng-template #loadedContent>
        <!-- Customer list: segment tabs, then the rows (table view) or nothing (grid view, cards below) -->
        <div
          class="orders-panel customer-table-card customers-panel mb-4"
          [class.is-tabs-only]="customers.length && viewMode === 'grid'"
        >
          <div
            class="status-tabs"
            role="tablist"
            [attr.aria-label]="'Customers' | translate"
            *ngIf="allCustomers.length || activeSegment !== 'ALL'"
          >
            <button
              type="button"
              role="tab"
              class="status-tab"
              *ngFor="let tab of segmentTabs"
              [class.active]="activeSegment === tab.value"
              [class.is-zero]="!getSegmentCount(tab.value)"
              [attr.aria-selected]="activeSegment === tab.value"
              [title]="tab.hint ? (tab.hint | translate) : ''"
              (click)="setSegment(tab.value)"
            >
              <i class="fi fi-rr-crown status-tab-icon" *ngIf="tab.value === 'VIP'" aria-hidden="true"></i>
              <span>{{ tab.label | translate }}</span>
              <span class="status-tab-count">{{ getSegmentCount(tab.value) }}</span>
            </button>
          </div>

          <!-- Empty State -->
          <div class="orders-empty" *ngIf="!customers.length">
            <div class="empty-icon-circle">
              <i class="fi fi-rr-users"></i>
            </div>
            <h3>{{ "No Customers Found" | translate }}</h3>
            <p>{{ "No customers matched your active filter or search criteria." | translate }}</p>
            <button
              type="button"
              class="btn btn-primary orders-empty-action"
              (click)="clearCustomerSearch(); activeSegment = 'ALL'; filteredCountry = ''; applyLocalFilters()"
            >
              <i class="fi fi-rr-refresh mr-1"></i>
              {{ "Reset All Filters" | translate }}
            </button>
          </div>

          <!-- Table view -->
          <ng-container *ngIf="customers.length && viewMode === 'table'">
            <div class="orders-head" aria-hidden="true">
              <span>{{ "Customer" | translate }}</span>
              <span>{{ "Contact & Location" | translate }}</span>
              <span>{{ "Orders (Lifetime)" | translate }}</span>
              <span class="text-right">{{ "Lifetime Spend" | translate }}</span>
              <span></span>
            </div>

            <div class="order-row customer-row" *ngFor="let customer of customers; let i = index" (click)="openCustomerFromRow(customer)">
              <!-- Customer -->
              <div class="order-cell col-customer">
                <div class="d-flex align-items-center">
                  <div class="customer-avatar-wrap" [style.background]="customer.avatar_gradient">
                    <span>{{ customer.avatar_initials }}</span>
                    <span
                      class="status-indicator"
                      [class.active]="customer.status === 'Active'"
                      [class.inactive]="customer.status === 'Inactive'"
                      [title]="customer.status === 'Active' ? ('Active customer' | translate) : ('Inactive customer (>60d)' | translate)"
                    ></span>
                  </div>

                  <div class="customer-name-box ml-3">
                    <div class="name-row d-flex align-items-center flex-wrap gap-2">
                      <button
                        type="button"
                        class="customer-name customer-name-link"
                        [title]="customer.customer_name"
                        (click)="$event.stopPropagation(); openCustomerDetail(customer)"
                      >
                        {{ customer.customer_name || "-" }}
                      </button>
                      <span class="tier-badge" [ngClass]="getTierBadgeClass(customer.lifecycle_badge || customer.tier)" [title]="(customer.lifecycle_badge || customer.tier) === 'VIP' ? ('Lifetime spend ≥ MYR 2,000 or ≥ 10 paid orders' | translate) : ''">
                        <i class="fi fi-rr-crown mr-1" *ngIf="(customer.lifecycle_badge || customer.tier) === 'VIP'"></i>
                        {{ (customer.lifecycle_badge || customer.tier) | translate }}
                      </span>
                      <span class="badge-new-tag" *ngIf="customer.is_new">
                        {{ "New" | translate }}
                      </span>
                    </div>
                    <div class="meta-subrow d-flex align-items-center mt-1">
                      <span class="customer-since text-muted text-xs">
                        {{ "Since" | translate }} {{ customer.first_order_date | date : 'd MMM y' }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Contact & Location -->
              <div class="order-cell col-contact" (click)="$event.stopPropagation()">
                <div class="contact-details-box">
                  <div class="location-pill mb-1 d-flex align-items-center">
                    <img [src]="getFlagImageURL(customer.country_code)" class="flag-icon mr-1.5" alt="flag" />
                    <span class="city-text">{{ formatLocation(customer.city, customer.state, customer.country_code) }}</span>
                  </div>

                  <div class="contact-line email-line d-flex align-items-center">
                    <i class="fi fi-rr-envelope text-muted mr-1.5"></i>
                    <ng-container *ngIf="customer.customer_email; else noEmailTable">
                      <a [href]="getMailtoLink(customer.customer_email)" class="email-link text-truncate" [title]="customer.customer_email">
                        {{ customer.customer_email }}
                      </a>
                      <button
                        type="button"
                        class="btn-copy-inline"
                        (click)="copyToClipboard(customer.customer_email, 'Email')"
                        title="{{ 'Copy Email' | translate }}"
                      >
                        <i class="fi fi-rr-copy"></i>
                      </button>
                    </ng-container>
                    <ng-template #noEmailTable>
                      <span class="no-email-text">{{ "No email" | translate }}</span>
                    </ng-template>
                  </div>

                  <div class="contact-line phone-line d-flex align-items-center mt-0.5">
                    <i class="fi fi-rr-phone-call text-muted mr-1.5"></i>
                    <span class="phone-text">
                      +({{ customer.customer_mobile_prefix || "60" }}) {{ customer.customer_mobile || "-" }}
                    </span>
                    <button
                      type="button"
                      class="btn-copy-inline ml-1"
                      *ngIf="customer.customer_mobile"
                      (click)="copyToClipboard('+' + (customer.customer_mobile_prefix || '60') + customer.customer_mobile, 'Phone Number')"
                      title="{{ 'Copy Phone' | translate }}"
                    >
                      <i class="fi fi-rr-copy"></i>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Orders & activity -->
              <div class="order-cell col-orders">
                <div class="order-activity-box">
                  <div class="order-count-pill mb-1">
                    <i class="fi fi-rr-shopping-bag mr-1 text-primary"></i>
                    <strong>{{ customer.lifetime_paid_orders }}</strong>
                    <span class="text-muted ml-1">{{ customer.lifetime_paid_orders === 1 ? ("Paid Order" | translate) : ("Paid Orders" | translate) }}</span>
                  </div>

                  <div class="activity-timeline-row text-xs text-muted" *ngIf="customer.order_breakdown?.length">
                    <span *ngFor="let part of customer.order_breakdown; let last = last">{{ part.count }} {{ part.label | translate }}<span *ngIf="!last"> · </span></span>
                  </div>

                  <div class="activity-timeline-row text-xs text-muted">
                    <span>{{ "Last purchase:" | translate }}</span>
                    <strong class="text-dark ml-1">{{ customer.last_order_relative }}</strong>
                  </div>

                  <div class="order-status-tag mt-1">
                    <span class="badge-status-pill" [ngClass]="getOrderStatusBadgeClass(customer.latest_order_status)">
                      <span class="status-dot"></span>
                      {{ "Last order:" | translate }} {{ customer.latest_order_status | translate }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Lifetime spend -->
              <div class="order-cell col-ltv text-right">
                <div class="financials-box">
                  <div class="total-spent-amount">
                    <span class="currency-symbol">{{ customer.currency || currency }}</span>
                    <strong>{{ customer.total_spent | number : '1.2-2' }}</strong>
                  </div>

                  <div class="aov-subtext text-muted text-xs mt-0.5" *ngFor="let other of customer.other_currency_spend">
                    + {{ other.currency }} {{ other.amount | number : '1.2-2' }}
                  </div>

                  <div class="aov-subtext text-muted text-xs mt-0.5" *ngIf="customer.pending_spend > 0">
                    + {{ customer.currency || currency }} {{ customer.pending_spend | number : '1.2-2' }} {{ "pending collection" | translate }}
                  </div>

                  <div class="aov-subtext text-muted text-xs mt-0.5">
                    {{ "Avg. order value:" | translate }} {{ customer.currency || currency }} {{ customer.average_order_value | number : '1.2-2' }}
                  </div>

                  <div class="payment-badge-wrap mt-1 d-flex justify-content-end">
                    <span class="payment-method-pill">
                      <i class="fi fi-rr-credit-card mr-1"></i>
                      {{ customer.preferred_payment }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- WhatsApp, open -->
              <div class="order-cell order-actions col-actions" (click)="$event.stopPropagation()">
                <a
                  *ngIf="customer.customer_mobile"
                  [href]="getWhatsAppLink(customer.customer_mobile, customer.customer_mobile_prefix)"
                  target="_blank"
                  class="btn btn-sm btn-icon-action btn-wa-action"
                  title="{{ 'Send WhatsApp Message' | translate }}"
                  [attr.aria-label]="'Send WhatsApp Message' | translate"
                >
                  <i class="fab fa-whatsapp"></i>
                </a>
                <button
                  type="button"
                  class="row-open"
                  [attr.aria-label]="'View Full Customer History' | translate"
                  [title]="'View Full Customer History' | translate"
                  (click)="openCustomerDetail(customer)"
                >
                  <i class="fi fi-rr-angle-small-right" aria-hidden="true"></i>
                </button>
              </div>
            </div>
          </ng-container>
        </div>

        <!-- VIEW 2: Grid Cards View -->
        <div class="customer-grid-container mb-4" *ngIf="customers.length && viewMode === 'grid'">
          <div class="row g-3">
            <div class="col-xl-4 col-md-6 col-12" *ngFor="let customer of customers; let i = index">
              <div class="customer-card shadow-sm" (click)="openCustomerDetail(customer)">
                <div class="customer-card-header d-flex justify-content-between align-items-start">
                  <div class="d-flex align-items-center">
                    <div class="customer-avatar-wrap" [style.background]="customer.avatar_gradient">
                      <span>{{ customer.avatar_initials }}</span>
                      <span
                        class="status-indicator"
                        [class.active]="customer.status === 'Active'"
                        [class.inactive]="customer.status === 'Inactive'"
                        [title]="customer.status === 'Active' ? ('Active customer' | translate) : ('Inactive customer (>60d)' | translate)"
                      ></span>
                    </div>
                    <div class="ml-3">
                      <h4 class="card-customer-name mb-0 text-truncate" [title]="customer.customer_name">
                        {{ customer.customer_name }}
                      </h4>
                      <span class="text-xs text-muted" *ngIf="customer.first_order_date">
                        {{ "Since" | translate }} {{ customer.first_order_date | date : 'd MMM y' }}
                      </span>
                    </div>
                  </div>
                  <div class="d-flex align-items-center gap-1.5 flex-wrap justify-content-end">
                    <span class="tier-badge" [ngClass]="getTierBadgeClass(customer.lifecycle_badge || customer.tier)" [title]="(customer.lifecycle_badge || customer.tier) === 'VIP' ? ('Lifetime spend ≥ MYR 2,000 or ≥ 10 paid orders' | translate) : ''">
                      <i class="fi fi-rr-crown mr-1" *ngIf="(customer.lifecycle_badge || customer.tier) === 'VIP'"></i>
                      {{ (customer.lifecycle_badge || customer.tier) | translate }}
                    </span>
                    <span class="badge-new-tag" *ngIf="customer.is_new">
                      {{ "New" | translate }}
                    </span>
                  </div>
                </div>

                <div class="customer-card-body mt-3">
                  <div class="card-contact-row d-flex align-items-center mb-2">
                    <img [src]="getFlagImageURL(customer.country_code)" class="flag-icon mr-2" alt="flag" />
                    <span class="text-xs text-dark">{{ formatLocation(customer.city, customer.state, customer.country_code) }}</span>
                  </div>

                  <div class="card-contact-row d-flex align-items-center mb-1 text-xs text-muted">
                    <i class="fi fi-rr-envelope mr-2"></i>
                    <span class="text-truncate" *ngIf="customer.customer_email; else noEmailGrid">{{ customer.customer_email }}</span>
                    <ng-template #noEmailGrid>
                      <span class="no-email-text">{{ "No email" | translate }}</span>
                    </ng-template>
                  </div>

                  <div class="card-contact-row d-flex align-items-center text-xs text-muted">
                    <i class="fi fi-rr-phone-call mr-2"></i>
                    <span>+({{ customer.customer_mobile_prefix }}) {{ customer.customer_mobile }}</span>
                  </div>

                  <div class="card-status-row mt-2">
                    <span class="badge-status-pill" [ngClass]="getOrderStatusBadgeClass(customer.latest_order_status)">
                      <span class="status-dot"></span>
                      {{ "Last order:" | translate }} {{ customer.latest_order_status | translate }}
                    </span>
                  </div>

                  <div class="card-metrics-grid mt-3 pt-3 border-top d-flex justify-content-between">
                    <div class="card-metric-col">
                      <span class="metric-label">{{ "Paid Orders" | translate }}</span>
                      <strong class="metric-value">{{ customer.lifetime_paid_orders }}</strong>
                      <span class="metric-label" *ngIf="customer.order_breakdown?.length">
                        <span *ngFor="let part of customer.order_breakdown; let last = last">{{ part.count }} {{ part.label | translate }}<span *ngIf="!last"> · </span></span>
                      </span>
                    </div>
                    <div class="card-metric-col text-right">
                      <span class="metric-label">{{ "Lifetime Spend" | translate }}</span>
                      <strong class="metric-value text-primary">
                        {{ customer.currency || currency }} {{ customer.total_spent | number : '1.2-2' }}
                      </strong>
                      <span class="metric-label" *ngFor="let other of customer.other_currency_spend">+ {{ other.currency }} {{ other.amount | number : '1.2-2' }}</span>
                      <span class="metric-label" *ngIf="customer.pending_spend > 0">
                        + {{ customer.currency || currency }} {{ customer.pending_spend | number : '1.2-2' }} {{ "pending collection" | translate }}
                      </span>
                    </div>
                  </div>
                </div>

                <div class="customer-card-footer mt-3 pt-2.5 border-top d-flex justify-content-between align-items-center" (click)="$event.stopPropagation()">
                  <span class="text-xs text-muted">
                    <i class="fi fi-rr-clock mr-1"></i> {{ customer.last_order_relative }}
                  </span>
                  <div class="d-flex gap-1.5">
                    <a
                      *ngIf="customer.customer_mobile"
                      [href]="getWhatsAppLink(customer.customer_mobile, customer.customer_mobile_prefix)"
                      target="_blank"
                      class="btn btn-sm btn-icon-action btn-wa-action"
                      title="{{ 'WhatsApp' | translate }}"
                    >
                      <i class="fab fa-whatsapp"></i>
                    </a>
                    <button
                      type="button"
                      class="btn-view-details"
                      (click)="openCustomerDetail(customer)"
                    >
                      <i class="fi fi-rr-eye mr-1"></i>
                      {{ "Details" | translate }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination Controls -->
        <div class="customer-pagination-bar d-flex justify-content-between align-items-center flex-wrap gap-3 py-3" *ngIf="customers.length">
          <!-- Pager first, then the count and the page size beneath it, the same
               order as every other list (the pager floats; see styles-system.scss). -->
          <div class="pagination-controls d-flex align-items-center gap-3">
            <!-- Page Navigation -->
            <ngb-pagination
              [collectionSize]="allCustomerLength"
              [(page)]="page"
              [pageSize]="selectedPage"
              [maxSize]="5"
              [rotate]="true"
              [ellipses]="false"
              (pageChange)="changePage($event)"
            ></ngb-pagination>
          </div>

          <div class="pagination-info text-muted text-xs">
            {{ "Showing" | translate }} <b>{{ start + 1 }}–{{ end }}</b> {{ "of" | translate }} <b>{{ allCustomerLength }}</b>
          </div>

          <!-- Page Size Selector -->
          <div class="page-size-picker d-flex align-items-center">
            <span class="text-xs text-muted mr-2">{{ "Per page:" | translate }}</span>
            <select
              class="form-select form-select-sm page-select"
              [ngModel]="selectedPage"
              (ngModelChange)="selectPage($event)"
            >
              <option [value]="10">10</option>
              <option [value]="20">20</option>
              <option [value]="50">50</option>
              <option [value]="100">100</option>
            </select>
          </div>
        </div>
      </ng-template>
    </div>
  </div>

  <!-- ========================================================================= -->
  <!-- VIEW 2: FULL-PAGE MIXPANEL-STYLE CUSTOMER PROFILE & JOURNEY ACTIVITY FEED -->
  <!-- ========================================================================= -->
  <div class="mixpanel-user-view" *ngIf="currentView === 'detail' && selectedCustomer">
    <!-- Top Header Bar with Back Button & Actions -->
    <div class="detail-header-bar mb-4">
      <div class="d-flex align-items-center justify-content-between flex-wrap gap-3">
        <div class="d-flex align-items-center gap-3">
          <button
            type="button"
            class="btn btn-back"
            (click)="closeCustomerDetail()"
            title="{{ 'Back to Customers List' | translate }}"
          >
            <i class="fi fi-rr-arrow-left"></i>
          </button>
          <div>
            <div class="breadcrumb-text text-muted text-xs mb-0.5">
              <span class="cursor-pointer hover-underline" (click)="closeCustomerDetail()">{{ "Customers" | translate }}</span>
              <span class="mx-1.5">/</span>
              <span>{{ "User Profile" | translate }}</span>
            </div>
            <div class="d-flex align-items-center gap-2">
              <h2 class="detail-page-title mb-0">{{ selectedCustomer.customer_name }}</h2>
              <span class="tier-badge" [ngClass]="getTierBadgeClass(selectedCustomer.lifecycle_badge || selectedCustomer.tier)" [title]="(selectedCustomer.lifecycle_badge || selectedCustomer.tier) === 'VIP' ? ('Lifetime spend ≥ MYR 2,000 or ≥ 10 paid orders' | translate) : ''">
                <i class="fi fi-rr-crown mr-1" *ngIf="(selectedCustomer.lifecycle_badge || selectedCustomer.tier) === 'VIP'"></i>
                {{ (selectedCustomer.lifecycle_badge || selectedCustomer.tier) | translate }}
              </span>
              <span class="badge-new-tag" *ngIf="selectedCustomer.is_new">
                {{ "New" | translate }}
              </span>
              <span class="badge-status-pill" [ngClass]="selectedCustomer.status === 'Active' ? 'badge-status-completed' : 'badge-status-processing'" [title]="selectedCustomer.status === 'Active' ? ('Active: Placed an order within the last 60 days' | translate) : ('Inactive: No order in the last 60–365 days' | translate)">
                <span class="status-dot"></span>
                {{ selectedCustomer.status | translate }}
              </span>
            </div>
          </div>
        </div>

        <div class="d-flex align-items-center gap-2">
          <a
            *ngIf="selectedCustomer.customer_mobile"
            [href]="getWhatsAppLink(selectedCustomer.customer_mobile, selectedCustomer.customer_mobile_prefix)"
            target="_blank"
            class="btn btn-success d-inline-flex align-items-center shadow-sm"
          >
            <i class="fab fa-whatsapp mr-1.5"></i>
            <span>{{ "WhatsApp Customer" | translate }}</span>
          </a>

          <a
            *ngIf="selectedCustomer.customer_email"
            [href]="getMailtoLink(selectedCustomer.customer_email)"
            class="btn btn-inverse-primary d-inline-flex align-items-center"
          >
            <i class="fi fi-rr-envelope mr-1.5"></i>
            <span>{{ "Send Email" | translate }}</span>
          </a>
        </div>
      </div>
    </div>

    <!-- Main Two-Column Layout (Matching Mixpanel Screenshot) -->
    <div class="row g-4 mixpanel-grid-container">
      <!-- LEFT COLUMN: User Profile & Properties (Mixpanel Style) -->
      <div class="col-lg-4 col-xl-4 col-12">
        <div class="profile-left-sidebar shadow-sm">
          <!-- Top Customer Summary Card -->
          <div class="customer-summary-card p-3 mx-3 mt-3 mb-2 rounded-3 bg-light border">
            <div class="d-flex justify-content-between align-items-center mb-2 pb-1 border-bottom">
              <span class="text-xs font-weight-bold text-uppercase text-muted">{{ "Customer Summary" | translate }}</span>
              <span class="tier-badge" [ngClass]="getTierBadgeClass(selectedCustomer.lifecycle_badge || selectedCustomer.tier)" [title]="(selectedCustomer.lifecycle_badge || selectedCustomer.tier) === 'VIP' ? ('Lifetime spend ≥ MYR 2,000 or ≥ 10 paid orders' | translate) : ''">
                <i class="fi fi-rr-crown mr-1" *ngIf="(selectedCustomer.lifecycle_badge || selectedCustomer.tier) === 'VIP'"></i>
                {{ (selectedCustomer.lifecycle_badge || selectedCustomer.tier) | translate }}
              </span>
            </div>
            <div class="summary-stats-grid">
              <div class="summary-stat-item">
                <span class="stat-label text-muted text-xs">{{ "Paid Orders" | translate }}</span>
                <span class="stat-val font-weight-bold text-dark">{{ selectedCustomer.lifetime_paid_orders }}</span>
                <span class="stat-label text-muted text-xs" *ngIf="selectedCustomer.order_breakdown?.length">
                  <span *ngFor="let part of selectedCustomer.order_breakdown; let last = last">{{ part.count }} {{ part.label | translate }}<span *ngIf="!last"> · </span></span>
                </span>
              </div>
              <div class="summary-stat-item">
                <span class="stat-label text-muted text-xs">{{ "Lifetime Spend" | translate }}</span>
                <span class="stat-val font-weight-bold text-primary">{{ selectedCustomer.currency || currency }} {{ selectedCustomer.total_spent | number : '1.2-2' }}</span>
                <span class="stat-label text-muted text-xs" *ngFor="let other of selectedCustomer.other_currency_spend">+ {{ other.currency }} {{ other.amount | number : '1.2-2' }}</span>
                <span class="stat-label text-muted text-xs" *ngIf="selectedCustomer.pending_spend > 0">
                  + {{ selectedCustomer.currency || currency }} {{ selectedCustomer.pending_spend | number : '1.2-2' }} {{ "pending collection" | translate }}
                </span>
              </div>
              <div class="summary-stat-item">
                <span class="stat-label text-muted text-xs">{{ "Avg. Order Value" | translate }}</span>
                <span class="stat-val font-weight-bold text-dark">{{ selectedCustomer.currency || currency }} {{ selectedCustomer.average_order_value | number : '1.2-2' }}</span>
              </div>
              <div class="summary-stat-item">
                <span class="stat-label text-muted text-xs">{{ "Preferred Payment" | translate }}</span>
                <span class="stat-val font-weight-medium text-dark">{{ selectedCustomer.preferred_payment }}</span>
              </div>
              <div class="summary-stat-item">
                <span class="stat-label text-muted text-xs">{{ "First Order" | translate }}</span>
                <span class="stat-val font-weight-medium text-dark">{{ selectedCustomer.first_order_date && selectedCustomer.first_order_date !== '—' ? (selectedCustomer.first_order_date | date : 'd MMM y') : '—' }}</span>
              </div>
              <div class="summary-stat-item">
                <span class="stat-label text-muted text-xs">{{ "Last Order" | translate }}</span>
                <span class="stat-val font-weight-medium text-dark">{{ selectedCustomer.last_order_date ? (selectedCustomer.last_order_date | date : 'd MMM y') : selectedCustomer.last_order_relative }}</span>
              </div>
            </div>
          </div>

          <!-- User Avatar & Identity Card -->
          <div class="user-identity-card p-4 text-center">
            <div
              class="user-avatar-circle mx-auto mb-3"
              [style.background]="selectedCustomer.avatar_gradient"
            >
              <span class="avatar-initials-lg text-white font-weight-bold">{{ selectedCustomer.avatar_initials }}</span>
            </div>
            <h3 class="user-title mb-1">{{ selectedCustomer.customer_name }}</h3>
            <p class="user-email text-muted mb-0 text-truncate" [title]="selectedCustomer.customer_email || ('No email' | translate)">
              <span *ngIf="selectedCustomer.customer_email; else noEmailProfile">{{ selectedCustomer.customer_email }}</span>
              <ng-template #noEmailProfile>
                <span class="no-email-text">{{ "No email" | translate }}</span>
              </ng-template>
            </p>
          </div>

          <!-- Phone Number Card -->
          <div class="profile-meta-item px-4 py-3 border-top" *ngIf="selectedCustomer.customer_mobile">
            <div class="meta-label d-flex align-items-center justify-content-between mb-1">
              <span class="d-flex align-items-center text-xs font-weight-semibold text-muted">
                <i class="fi fi-rr-phone-call mr-1.5 text-primary"></i>
                {{ "Phone Number" | translate }}
              </span>
              <button
                type="button"
                class="btn-copy-link"
                (click)="copyToClipboard('+' + (selectedCustomer.customer_mobile_prefix || '60') + selectedCustomer.customer_mobile, 'Phone Number')"
                title="{{ 'Copy Phone Number' | translate }}"
              >
                <i class="fi fi-rr-copy"></i>
              </button>
            </div>
            <div class="meta-value font-weight-medium text-dark text-sm">
              +({{ selectedCustomer.customer_mobile_prefix || '60' }}) {{ selectedCustomer.customer_mobile }}
            </div>
          </div>

          <!-- Location Card -->
          <div class="profile-meta-item px-4 py-3 border-top">
            <div class="meta-label d-flex align-items-center mb-1 text-xs font-weight-semibold text-muted">
              <i class="fi fi-rr-marker mr-1.5 text-primary"></i>
              {{ "Location" | translate }}
            </div>
            <div class="meta-value font-weight-medium text-dark text-sm d-flex align-items-center">
              <img
                [src]="getFlagImageURL(selectedCustomer.country_code)"
                class="mr-2 flag-icon"
                alt="flag"
              />
              <span>
                {{ formatLocation(selectedCustomer.city, selectedCustomer.state, selectedCustomer.country_code) }}
              </span>
            </div>
          </div>

          <!-- Updated At Card -->
          <div class="profile-meta-item px-4 py-3 border-top">
            <div class="meta-label d-flex align-items-center mb-1 text-xs font-weight-semibold text-muted">
              <i class="fi fi-rr-calendar mr-1.5 text-primary"></i>
              {{ "Last Active" | translate }}
            </div>
            <div class="meta-value font-weight-medium text-dark text-sm">
              {{ selectedCustomer.last_order_relative }}
            </div>
          </div>

          <!-- User Profile Properties Section -->
          <div class="user-properties-section border-top" *ngIf="customerPropertiesList && customerPropertiesList.length > 0">
            <div class="properties-header px-4 pt-3 pb-2 d-flex justify-content-between align-items-center">
              <h5 class="properties-title mb-0">{{ "User Profile Properties" | translate }}</h5>
              <span class="properties-count-badge">{{ filteredPropertiesList.length }}</span>
            </div>

            <!-- Property Search Box -->
            <div class="property-search-wrap px-4 py-2">
              <div class="property-search-input-box">
                <i class="fi fi-rr-search search-prop-icon"></i>
                <input
                  type="text"
                  class="form-control form-control-sm prop-search-input"
                  [(ngModel)]="propertySearchQuery"
                  (input)="filterProperties()"
                  placeholder="{{ 'Search properties' | translate }}"
                />
                <i class="fas fa-times clear-prop-icon" *ngIf="propertySearchQuery" (click)="propertySearchQuery = ''; filterProperties()"></i>
              </div>
            </div>

            <!-- Properties List -->
            <div class="properties-list-box px-4 py-2">
              <div class="property-row py-2 border-bottom" *ngFor="let prop of filteredPropertiesList">
                <div class="d-flex justify-content-between align-items-start gap-2">
                  <span class="prop-key text-xs font-weight-semibold" [class.system-key]="prop.systemProperty">
                    <i [class]="prop.icon + ' mr-1 text-muted'" *ngIf="prop.icon"></i>
                    {{ prop.key }}
                  </span>
                  <div class="d-flex align-items-center">
                    <span class="prop-val text-xs text-dark text-right text-truncate" [title]="prop.value">
                      {{ prop.value }}
                    </span>
                    <button
                      type="button"
                      class="btn-copy-prop ml-1.5"
                      (click)="copyToClipboard(prop.value.toString(), prop.key)"
                      title="{{ 'Copy' | translate }}"
                    >
                      <i class="fi fi-rr-copy"></i>
                    </button>
                  </div>
                </div>
              </div>

              <div class="text-center py-4 text-muted text-xs" *ngIf="!filteredPropertiesList.length">
                {{ "No matching properties" | translate }}
              </div>
            </div>
          </div>

          <!-- Collapsible Additional Details Section -->
          <div class="technical-properties-section border-top">
            <div
              class="properties-header px-4 py-3 d-flex justify-content-between align-items-center cursor-pointer"
              (click)="toggleTechnicalDetails()"
            >
              <div class="d-flex align-items-center gap-2">
                <i class="fi fi-rr-settings-sliders text-muted text-xs"></i>
                <h5 class="properties-title mb-0 text-xs font-weight-bold text-muted text-uppercase">{{ "Additional Details" | translate }}</h5>
              </div>
              <div class="d-flex align-items-center gap-2">
                <span class="properties-count-badge">{{ filteredTechnicalPropertiesList.length }}</span>
                <i class="fi" [ngClass]="isTechnicalDetailsOpen ? 'fi-rr-angle-small-up' : 'fi-rr-angle-small-down'"></i>
              </div>
            </div>

            <div class="properties-list-box px-4 py-2" *ngIf="isTechnicalDetailsOpen">
              <div class="property-row py-2 border-bottom" *ngFor="let prop of filteredTechnicalPropertiesList">
                <div class="d-flex justify-content-between align-items-start gap-2">
                  <span class="prop-key text-xs font-weight-semibold system-key">
                    <i [class]="prop.icon + ' mr-1 text-muted'" *ngIf="prop.icon"></i>
                    {{ prop.key }}
                  </span>
                  <div class="d-flex align-items-center">
                    <span class="prop-val text-xs text-dark text-right text-truncate" [title]="prop.value">
                      {{ prop.value }}
                    </span>
                    <button
                      type="button"
                      class="btn-copy-prop ml-1.5"
                      (click)="copyToClipboard(prop.value.toString(), prop.key)"
                      title="{{ 'Copy' | translate }}"
                    >
                      <i class="fi fi-rr-copy"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- RIGHT COLUMN: Mixpanel Activity Feed / Customer Journey -->
      <div class="col-lg-8 col-xl-8 col-12">
        <div class="activity-feed-card shadow-sm">
          <!-- Feed Header & Mixpanel Toolbar Actions -->
          <div class="feed-header-bar p-3.5 border-bottom d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div class="d-flex align-items-center gap-2">
              <h4 class="feed-title mb-0">{{ "Activity Feed" | translate }}</h4>
              <span class="feed-count-pill">{{ customerEvents.length }} {{ "events" | translate }}</span>
            </div>

            <!-- Right Toolbar Buttons (Matching Screenshot) -->
            <div class="feed-toolbar-actions d-flex align-items-center gap-2 flex-wrap">
              <!-- Watch Replays Button -->
              <button
                type="button"
                class="btn btn-feed-tool"
                routerLink="/dashboard/replay"
                title="{{ 'Watch Visitor Session Replays' | translate }}"
              >
                <i class="fi fi-rr-video-camera mr-1.5 text-primary"></i>
                <span>{{ "Watch Replays" | translate }}</span>
              </button>

              <!-- View in Insights Button -->
              <button
                type="button"
                class="btn btn-feed-tool"
                routerLink="/dashboard/visitor-intelligence"
                title="{{ 'View in Visitor Insights' | translate }}"
              >
                <i class="fi fi-rr-chart-histogram mr-1.5 text-success"></i>
                <span>{{ "View in Insights" | translate }}</span>
              </button>

              <!-- Date Filter Pill (e.g. Last 30 days) -->
              <div ngbDropdown class="d-inline-block">
                <button class="btn btn-feed-tool" ngbDropdownToggle id="dropdownFeedRange">
                  <i class="fi fi-rr-calendar mr-1.5 text-amber-500"></i>
                  <span>{{ activeTimelineRange === '30d' ? ('Last 30 days' | translate) : (activeTimelineRange === '7d' ? ('Last 7 days' | translate) : ('All Time' | translate)) }}</span>
                </button>
                <div ngbDropdownMenu aria-labelledby="dropdownFeedRange" class="custom-dropdown shadow-md">
                  <button ngbDropdownItem (click)="setTimelineRange('7d')" [class.active]="activeTimelineRange === '7d'">
                    {{ "Last 7 days" | translate }}
                  </button>
                  <button ngbDropdownItem (click)="setTimelineRange('30d')" [class.active]="activeTimelineRange === '30d'">
                    {{ "Last 30 days" | translate }}
                  </button>
                  <button ngbDropdownItem (click)="setTimelineRange('all')" [class.active]="activeTimelineRange === 'all'">
                    {{ "All Time" | translate }}
                  </button>
                </div>
              </div>

              <!-- Filter / Hide Events Button -->
              <div ngbDropdown class="d-inline-block">
                <button class="btn btn-feed-tool" ngbDropdownToggle id="dropdownHideEvents">
                  <i class="fi fi-rr-eye-crossed mr-1.5 text-muted"></i>
                  <span>{{ "Hide Events" | translate }}</span>
                  <span class="badge badge-primary ml-1" *ngIf="hiddenCategories.size > 0">{{ hiddenCategories.size }}</span>
                </button>
                <div ngbDropdownMenu aria-labelledby="dropdownHideEvents" class="custom-dropdown p-2 shadow-lg" style="min-width: 240px;">
                  <div class="dropdown-header text-xs text-uppercase font-weight-bold px-2 py-1 text-muted">
                    {{ "Toggle Event Categories" | translate }}
                  </div>
                  <button ngbDropdownItem (click)="toggleEventCategory('commerce')" class="d-flex align-items-center justify-content-between py-1.5">
                    <span class="d-flex align-items-center"><i class="fi fi-rr-shopping-bag mr-2 text-success"></i> Commerce & Orders</span>
                    <i class="fi fi-rr-check text-primary" *ngIf="!isCategoryHidden('commerce')"></i>
                    <span class="text-xs text-muted" *ngIf="isCategoryHidden('commerce')">{{ "Hidden" | translate }}</span>
                  </button>
                  <button ngbDropdownItem (click)="toggleEventCategory('screen')" class="d-flex align-items-center justify-content-between py-1.5">
                    <span class="d-flex align-items-center"><i class="fi fi-rr-browser mr-2 text-primary"></i> Screen & Discovery</span>
                    <i class="fi fi-rr-check text-primary" *ngIf="!isCategoryHidden('screen')"></i>
                    <span class="text-xs text-muted" *ngIf="isCategoryHidden('screen')">{{ "Hidden" | translate }}</span>
                  </button>
                  <button ngbDropdownItem (click)="toggleEventCategory('marketing')" class="d-flex align-items-center justify-content-between py-1.5">
                    <span class="d-flex align-items-center"><i class="fi fi-rr-bullseye-arrow mr-2 text-warning"></i> Marketing & Promo</span>
                    <i class="fi fi-rr-check text-primary" *ngIf="!isCategoryHidden('marketing')"></i>
                    <span class="text-xs text-muted" *ngIf="isCategoryHidden('marketing')">{{ "Hidden" | translate }}</span>
                  </button>
                  <button ngbDropdownItem (click)="toggleEventCategory('delivery')" class="d-flex align-items-center justify-content-between py-1.5">
                    <span class="d-flex align-items-center"><i class="fi fi-rr-truck-moving mr-2 text-info"></i> Delivery & Shipping</span>
                    <i class="fi fi-rr-check text-primary" *ngIf="!isCategoryHidden('delivery')"></i>
                    <span class="text-xs text-muted" *ngIf="isCategoryHidden('delivery')">{{ "Hidden" | translate }}</span>
                  </button>
                  <button ngbDropdownItem (click)="toggleEventCategory('communication')" class="d-flex align-items-center justify-content-between py-1.5">
                    <span class="d-flex align-items-center"><i class="fab fa-whatsapp mr-2 text-success"></i> WhatsApp & Comms</span>
                    <i class="fi fi-rr-check text-primary" *ngIf="!isCategoryHidden('communication')"></i>
                    <span class="text-xs text-muted" *ngIf="isCategoryHidden('communication')">{{ "Hidden" | translate }}</span>
                  </button>
                  <button ngbDropdownItem (click)="toggleEventCategory('api')" class="d-flex align-items-center justify-content-between py-1.5">
                    <span class="d-flex align-items-center"><i class="fi fi-rr-code-compare mr-2 text-purple"></i> API Calls</span>
                    <i class="fi fi-rr-check text-primary" *ngIf="!isCategoryHidden('api')"></i>
                    <span class="text-xs text-muted" *ngIf="isCategoryHidden('api')">{{ "Hidden" | translate }}</span>
                  </button>
                  <button ngbDropdownItem (click)="toggleEventCategory('session')" class="d-flex align-items-center justify-content-between py-1.5">
                    <span class="d-flex align-items-center"><i class="fi fi-rr-mobile-button mr-2 text-secondary"></i> App Sessions</span>
                    <i class="fi fi-rr-check text-primary" *ngIf="!isCategoryHidden('session')"></i>
                    <span class="text-xs text-muted" *ngIf="isCategoryHidden('session')">{{ "Hidden" | translate }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Feed Search Box -->
          <div class="feed-search-bar px-4 py-2.5 bg-light border-bottom">
            <div class="position-relative">
              <i class="fi fi-rr-search position-absolute text-muted" style="left: 12px; top: 10px; font-size: 13px;"></i>
              <input
                type="text"
                class="form-control form-control-sm pl-4 pr-4"
                [(ngModel)]="eventSearchQuery"
                (input)="filterEvents()"
                placeholder="{{ 'Filter events by name, properties, tracking ID, or payload...' | translate }}"
              />
              <i class="fas fa-times position-absolute text-muted cursor-pointer" style="right: 12px; top: 10px; font-size: 12px;" *ngIf="eventSearchQuery" (click)="eventSearchQuery = ''; filterEvents()"></i>
            </div>
          </div>

          <!-- Activity Feed Grouped List (Mixpanel Style) -->
          <div class="activity-feed-body p-4">
            <div class="date-group-block mb-4" *ngFor="let group of groupedEvents">
              <!-- Date Heading Header -->
              <div class="date-group-heading mb-3 d-flex align-items-center">
                <i class="fi fi-rr-calendar-clock mr-2 text-primary"></i>
                <span class="group-title-text">{{ group.date_group }}</span>
              </div>

              <!-- Events in this group -->
              <div class="events-timeline-list">
                <div
                  class="mixpanel-event-row"
                  *ngFor="let event of group.events"
                  [class.is-expanded]="event.expanded"
                >
                  <div
                    class="event-summary-row d-flex align-items-center justify-content-between p-3"
                    (click)="toggleEventAccordion(event)"
                  >
                    <div class="d-flex align-items-center gap-3 flex-wrap">
                      <!-- Timestamp -->
                      <span class="event-timestamp text-xs text-muted font-weight-mono">
                        {{ event.timestamp }}
                      </span>

                      <!-- Category Badge -->
                      <span
                        class="event-category-pill"
                        [ngClass]="event.category_color || 'badge-cat-screen'"
                        *ngIf="event.category_label"
                      >
                        <i [class]="event.category_icon + ' mr-1'" *ngIf="event.category_icon"></i>
                        {{ event.category_label }}
                      </span>

                      <!-- Event Name & Count Badge -->
                      <div class="d-flex align-items-center gap-2">
                        <span class="event-name-text font-weight-semibold">
                          {{ event.display_name || (event.event_name === 'Order_Completed' ? ('Order placed' | translate) : event.event_name) }}
                        </span>
                        <span class="event-badge-count" *ngIf="event.badge_count">
                          {{ event.badge_count }}
                        </span>
                        <span class="badge-status-pill" [ngClass]="getOrderStatusBadgeClass(event.status_badge)" *ngIf="event.status_badge">
                          <span class="status-dot"></span>
                          {{ event.status_badge }}
                        </span>
                      </div>
                    </div>

                    <!-- Right Controls: Action Trigger + Accordion Indicator -->
                    <div class="d-flex align-items-center gap-2" (click)="$event.stopPropagation()">
                      <!-- Interactive Action Link (Replay / WhatsApp / Copy) -->
                      <ng-container *ngIf="event.action_type === 'replay'">
                        <a
                          [routerLink]="['/dashboard/replay']"
                          [queryParams]="{ visitor: selectedCustomer.customer_id }"
                          class="btn btn-sm btn-event-action btn-action-replay"
                          title="{{ 'Watch Replay for this session' | translate }}"
                        >
                          <i class="fi fi-rr-play mr-1"></i>
                          <span>{{ event.action_label | translate }}</span>
                        </a>
                      </ng-container>

                      <ng-container *ngIf="event.action_type === 'whatsapp'">
                        <a
                          [href]="event.action_value"
                          target="_blank"
                          class="btn btn-sm btn-event-action btn-action-wa"
                          title="{{ 'Chat on WhatsApp' | translate }}"
                        >
                          <i class="fab fa-whatsapp mr-1"></i>
                          <span>{{ event.action_label | translate }}</span>
                        </a>
                      </ng-container>

                      <ng-container *ngIf="event.action_type === 'copy'">
                        <div class="d-inline-flex align-items-center gap-1.5">
                          <button
                            type="button"
                            class="btn btn-sm btn-event-action btn-action-copy"
                            (click)="copyToClipboard(event.action_value, 'Order ID')"
                            title="{{ 'Copy order ID' | translate }}"
                          >
                            <i class="fi fi-rr-copy mr-1"></i>
                            <span>{{ event.action_label | translate }}</span>
                          </button>
                        </div>
                      </ng-container>

                      <div
                        class="event-expand-icon text-muted cursor-pointer p-1"
                        (click)="toggleEventAccordion(event)"
                      >
                        <i class="fi fi-rr-angle-small-down" *ngIf="!event.expanded"></i>
                        <i class="fi fi-rr-angle-small-up" *ngIf="event.expanded"></i>
                      </div>
                    </div>
                  </div>

                  <!-- Expanded Key-Value Properties Drawer (Mixpanel Inspector) -->
                  <div class="event-properties-drawer p-3.5 border-top" *ngIf="event.expanded">
                    <div class="properties-table-responsive">
                      <table class="table table-sm table-properties mb-0">
                        <tbody>
                          <tr *ngFor="let entry of event.properties | keyvalue">
                            <td class="prop-key-cell font-weight-mono text-xs text-muted" style="width: 35%;">
                              {{ entry.key }}
                            </td>
                            <td class="prop-val-cell font-weight-medium text-xs text-dark" style="width: 65%;">
                              {{ entry.value }}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="text-center py-5 text-muted" *ngIf="!groupedEvents.length">
              <i class="fi fi-rr-search mb-2 d-block" style="font-size: 28px;"></i>
              <p class="mb-0">{{ "No activity events match your filter." | translate }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
